import clsx from 'clsx';
import { forwardRef, useEffect, useId, type HTMLAttributes } from 'react';

import {
  type DataTestId,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { useBreakpoint } from '@dynatrace/strato-components/layouts';

import { PanelContext } from './contexts/PanelContext.js';
import { useConditionalPagePanel } from './hooks/useConditionalPagePanel.js';
import { usePageContext } from './hooks/usePageContext.js';
import { detailViewCSS, pageDrawerCSS } from './Page.sty.js';
import {
  BREAKPOINTS,
  MIN_WIDTHS,
  PANEL_ORDER,
  PREFERRED_WIDTHS,
} from './Page.js';
import { usePanelsContext } from './Panel/hooks/_usePanelsContext.js';
import { Panel } from './Panel/Panel.js';
import { Resizer } from './Panel/Resizer.js';
import type { PanelSize } from './Panel/types/shared-panel-types.js';
import type {
  PagePanelProps,
  PagePanelSizes,
} from './types/shared-page-types.js';
import { KeepMounted } from '../../core/components/keep-mounted/KeepMounted.js';
import { Drawer } from '../../overlays/drawer/Drawer.js';

//#region Props
/**
 * @public
 */
export interface DetailViewOwnProps
  extends WithChildren,
    Omit<HTMLAttributes<HTMLDivElement>, 'onResize' | keyof StylingProps>,
    StylingProps,
    DataTestId {
  /**
   * The disired width of the detail view.
   * Depending on the available screen width and other panel configurations,
   * the size of the panel might not match the `preferredWidth` exactly.
   * @defaultValue 25%
   */
  preferredWidth?: PanelSize;
  /**
   * The minimum width of the detail view in pixels.
   * @defaultValue 320
   */
  minWidth?: number;
  /**
   * Screen size below which the overlay Drawer will be shown.
   * @defaultValue 1280
   */
  breakpoint?: number;
  /**
   * If the detail views width can be resized (by drag and drop)
   * @defaultValue true
   */
  resizable?: boolean;
  /**
   * Whether to keep the detail view content mounted even if the detail view
   * is dismissed.
   * @defaultValue false
   */
  keepMounted?: boolean;
  /**
   * Callback triggered after resizing is finished with the new panel sizes of
   * all existing panels and the newly applied panel width (in px).
   */
  onResize?: (sizes: PagePanelSizes, width: number) => void;
  /**
   * Notifies the user if an action happened that should change the dismissed state
   * like clicking on the backdrop on mobile.
   * The returned state is only a recommendation and might differ from what the actual
   * state of the panel should be.
   */
  onDismissChange?: (state: boolean, reason: string) => void;
}

/**
 * @public
 */
export type DetailViewProps = DetailViewOwnProps & PagePanelProps;

//#endregion

/**
 * The `DetailView` component is used inside the `Page` component for displaying content in the corresponding panel.
 * @public
 */
export const DetailView = forwardRef<HTMLDivElement, DetailViewProps>(
  (props, forwardedRef) => {
    const {
      children,
      dismissed,
      defaultDismissed,
      onDismissChange,
      preferredWidth = PREFERRED_WIDTHS.details,
      minWidth = MIN_WIDTHS.details,
      breakpoint = BREAKPOINTS.details,
      resizable = true,
      onResize,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      keepMounted = false,
      ...remainingProps
    }: DetailViewProps = props;

    const id = useId();
    const showPanel = useBreakpoint(`(min-width: ${breakpoint}px)`, false);

    // Register the panel with the page and set its initial state.
    useConditionalPagePanel({
      name: 'details',
      defaultDismissed,
      dismissed,
      showPanel,
      breakpoint,
    });

    const { disableAnimations } = usePanelsContext();
    const { detailsDismissed, setPanelDismissed } = usePageContext();
    const isControlled = dismissed !== undefined;

    useEffect(() => {
      const reason = showPanel ? 'above breakpoint' : 'below breakpoint';
      onDismissChange?.(!showPanel, reason);

      // Excluding onDismissChange to trigger the effect only if the screen size reaches the breakpoint.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPanel]);

    // The drawer handels the dismissing on its own, to ensure the proper animation, therefore, the check excludes the mobile view.
    // If the panel is controlled, we check the dismissed prop directly to make sure the panel is instantly added to the state when
    // mounting. Since the default state of the panel in the context is set to 'dismissed', the layout effect in the parent panel would
    // else be triggered twice. Initially, only with the 'main' panel, and then again to add the 'sidebar'.
    if ((isControlled && dismissed) || (!isControlled && detailsDismissed)) {
      if (showPanel) {
        return null;
      } else {
        return keepMounted ? (
          <div style={{ display: 'none' }}>
            <KeepMounted id={id}>{children}</KeepMounted>
          </div>
        ) : null;
      }
    }

    return (
      <PanelContext.Provider value={{ panelName: 'details' }}>
        {showPanel ? (
          <>
            <Resizer
              data-testid="page-details-resize-handle"
              order={PANEL_ORDER.details}
              resizable={resizable}
              name="details-resize-handle"
            />
            <Panel
              {...remainingProps}
              onResize={(sizes, width) => {
                onResize?.(sizes as PagePanelSizes, width);
              }}
              ref={forwardedRef}
              name="details"
              order={PANEL_ORDER.details}
              preferredWidth={preferredWidth}
              minWidth={minWidth}
              resizable={resizable}
              dismissible="right"
              className={clsx(consumerClassName, detailViewCSS)}
              style={consumerStyle}
              data-testid={dataTestId}
            >
              {keepMounted ? (
                <KeepMounted id={id}>{children}</KeepMounted>
              ) : (
                children
              )}
            </Panel>
          </>
        ) : (
          <Drawer
            placement="right"
            width="wide"
            isDismissed={detailsDismissed}
            disableAnimations={disableAnimations}
            onDismiss={(state, reason) => {
              // Close panel if it is uncontrolled and the user clicks on the backdrop.
              if (
                dismissed === undefined &&
                state === true &&
                (reason === 'backdrop' || reason === 'escape')
              ) {
                setPanelDismissed('details', true);
              }
              onDismissChange?.(state, reason);
            }}
            className={clsx(pageDrawerCSS, consumerClassName)}
            style={consumerStyle}
            data-testid={dataTestId}
            {...remainingProps}
          >
            {keepMounted ? (
              <KeepMounted id={id}>{children}</KeepMounted>
            ) : (
              children
            )}
          </Drawer>
        )}
      </PanelContext.Provider>
    );
  },
);

(DetailView as typeof DetailView & { displayName: string }).displayName =
  'Page.DetailView';
